import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { AppSidebar } from "@/components/AppSidebar";
import { AppHeader } from "@/components/AppHeader";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { TeamPageHeader } from "@/components/team/TeamPageHeader";
import { TeamPickerModal } from "@/components/team/TeamPickerModal";
import { RosterView } from "@/components/team/RosterView";
import { ContextWidgets } from "@/components/team/ContextWidgets";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";

interface League {
  id: string;
  name: string;
  max_teams: number;
  created_at: string;
}

interface Team {
  id: string;
  user_id: string;
  league_id: string;
  league?: League;
  owner_name?: string;
}

interface GameweekInfo {
  id: string;
  gw_number: number;
  lock_at: string;
  status: string;
}

interface EditabilityInfo {
  editable: boolean;
  reason?: string;
  lockAt: string;
  status: string;
}

export default function Team() {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [selectedLeague, setSelectedLeague] = useState<League | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [userLeagues, setUserLeagues] = useState<League[]>([]);
  const [teamsInLeague, setTeamsInLeague] = useState<Team[]>([]);
  const [currentGameweek, setCurrentGameweek] = useState<GameweekInfo | null>(null);
  const [editabilityInfo, setEditabilityInfo] = useState<EditabilityInfo | null>(null);
  
  const [loading, setLoading] = useState(true);
  const [showTeamPicker, setShowTeamPicker] = useState(false);

  // Fetch user's leagues
  const fetchUserLeagues = async () => {
    if (!user) return;

    try {
      const { data: leagueData, error } = await supabase
        .from('league_players')
        .select(`
          league_id,
          leagues!inner (
            id,
            name,
            max_teams,
            created_at
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;

      const leagues = leagueData?.map(item => item.leagues).filter(Boolean) as League[] || [];
      setUserLeagues(leagues);

      // Auto-select first league if none selected
      if (!selectedLeague && leagues.length > 0) {
        setSelectedLeague(leagues[0]);
      }
    } catch (error) {
      console.error('Error fetching leagues:', error);
      toast({
        title: "Error",
        description: "Failed to load your leagues",
        variant: "destructive"
      });
    }
  };

  // Fetch teams in selected league
  const fetchTeamsInLeague = async (leagueId: string) => {
    try {
      // First get league players
      const { data: leaguePlayersData, error: leaguePlayersError } = await supabase
        .from('league_players')
        .select('id, user_id, league_id')
        .eq('league_id', leagueId);

      if (leaguePlayersError) throw leaguePlayersError;

      // Then get profile info for each user
      const userIds = leaguePlayersData?.map(lp => lp.user_id) || [];
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, username, display_name')
        .in('user_id', userIds);

      if (profilesError) throw profilesError;

      // Combine the data
      const teams = leaguePlayersData?.map(item => {
        const profile = profilesData?.find(p => p.user_id === item.user_id);
        return {
          id: item.id,
          user_id: item.user_id,
          league_id: item.league_id,
          owner_name: profile?.display_name || profile?.username || 'Unknown'
        };
      }) || [];

      setTeamsInLeague(teams as Team[]);

      // Auto-select user's team if available
      const userTeam = teams.find(team => team.user_id === user?.id);
      if (userTeam) {
        setSelectedTeam(userTeam as Team);
      } else if (teams.length === 1) {
        setSelectedTeam(teams[0] as Team);
      } else if (teams.length > 1) {
        setShowTeamPicker(true);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast({
        title: "Error", 
        description: "Failed to load teams in league",
        variant: "destructive"
      });
    }
  };

  // Fetch current gameweek
  const fetchCurrentGameweek = async (leagueId: string) => {
    try {
      const { data, error } = await supabase
        .from('gameweeks')
        .select('id, gw_number, lock_at, status')
        .eq('league_id', leagueId)
        .eq('status', 'upcoming')
        .maybeSingle();

      if (error) throw error;
      setCurrentGameweek(data);
    } catch (error) {
      console.error('Error fetching gameweek:', error);
    }
  };

  // Check roster editability
  const checkEditability = async (teamId: string, gameweekId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('can-edit-roster', {
        body: {
          leagueTeamId: teamId,
          gameweekId: gameweekId
        }
      });

      if (error) {
        console.error('Error checking editability:', error);
        // Default to editable for own team if check fails
        setEditabilityInfo({
          editable: true, // Allow editing by default for better UX
          reason: 'Edit permissions check failed',
          lockAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours from now
          status: 'draft'
        });
        return;
      }
      setEditabilityInfo(data);
    } catch (error) {
      console.error('Error checking editability:', error);
      // Default to editable for own team if check fails
      setEditabilityInfo({
        editable: true,
        reason: 'Edit permissions check failed',
        lockAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        status: 'draft'
      });
    }
  };

  // Handle league selection
  const handleLeagueSelect = (league: League) => {
    setSelectedLeague(league);
    setSelectedTeam(null);
    setTeamsInLeague([]);
    setCurrentGameweek(null);
    setEditabilityInfo(null);
  };

  // Handle team selection
  const handleTeamSelect = (team: Team) => {
    setSelectedTeam(team);
    setShowTeamPicker(false);
  };

  // Effects
  useEffect(() => {
    fetchUserLeagues();
  }, [user]);

  useEffect(() => {
    if (selectedLeague) {
      fetchTeamsInLeague(selectedLeague.id);
      fetchCurrentGameweek(selectedLeague.id);
    }
  }, [selectedLeague]);

  useEffect(() => {
    if (selectedTeam && currentGameweek) {
      checkEditability(selectedTeam.id, currentGameweek.id);
    }
  }, [selectedTeam, currentGameweek]);

  useEffect(() => {
    // Check if we have all required data for loading to complete
    if (selectedLeague && selectedTeam) {
      // Even if no gameweek is found, we can still show the team
      setLoading(false);
    }
  }, [selectedLeague, selectedTeam]);

  if (!user) return null;

  return (
    <SidebarProvider defaultOpen>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        <SidebarInset className="flex-1">
          <AppHeader />
          
          <main className="flex-1 p-6">
            <TeamPageHeader
              selectedLeague={selectedLeague}
              selectedTeam={selectedTeam}
              userLeagues={userLeagues}
              teamsInLeague={teamsInLeague}
              onLeagueSelect={handleLeagueSelect}
              onTeamSelect={handleTeamSelect}
            />

            {loading ? (
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <Skeleton className="h-96 w-full" />
                </div>
                <div className="space-y-4">
                  <Skeleton className="h-32 w-full" />
                  <Skeleton className="h-32 w-full" />
                  <Skeleton className="h-32 w-full" />
                </div>
              </div>
            ) : selectedLeague && selectedTeam ? (
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Roster View */}
                <div className="lg:col-span-2">
                  <RosterView
                    team={selectedTeam}
                    league={selectedLeague}
                    gameweek={currentGameweek}
                    editability={editabilityInfo}
                    isOwnTeam={selectedTeam.user_id === user.id}
                  />
                </div>

                {/* Right Column - Context Widgets */}
                <div>
                  <ContextWidgets
                    team={selectedTeam}
                    league={selectedLeague}
                    gameweek={currentGameweek}
                  />
                </div>
              </div>
            ) : (
              <div className="mt-6 text-center py-12">
                <p className="text-muted-foreground">
                  {!selectedLeague 
                    ? "Select a league to view teams" 
                    : "No teams found in this league"}
                </p>
              </div>
            )}

            {/* Team Picker Modal */}
            <TeamPickerModal
              isOpen={showTeamPicker}
              onClose={() => setShowTeamPicker(false)}
              league={selectedLeague}
              teams={teamsInLeague}
              onTeamSelect={handleTeamSelect}
              userTeam={teamsInLeague.find(t => t.user_id === user?.id)}
            />
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}